import { MissionContext } from 'sms-dashboard';

type VisualLineOfSight = 'VLOS' | 'EVLOS' | 'BVLOS';
type TimeOfDay = 'DAY' | 'NIGHT' | 'DAY_NIGHT';

interface RpaLike {
    id: number;
    rpaTypeId?: number;
    nickname?: string;
}

interface RpaTypeLike {
    id: number;
    make?: string;
    model?: string;
    performanceSpecifications?: { maxTakeOffWeight?: number };
}

interface CrewDetailLike {
    person?: { id?: number };
    role?: { coreRole?: string };
}

export interface MissionLike {
    crafts?: RpaLike[] | null;
    rpaTypes?: Record<number, RpaTypeLike>;
    missionCrewDetails?: CrewDetailLike[] | null;
    visualLineOfSight?: VisualLineOfSight;
    timeOfDay?: TimeOfDay;
    maximumHeight?: number | null;
    operationType?: string | null;
    locationName?: string | null;
}

export interface LiveMissionContextValue {
    craftIds: number[];
    maximumHeight?: number;
    visualLineOfSight?: VisualLineOfSight;
    timeOfDay?: TimeOfDay;
    operationType?: string | null;
    missionObjectivesGroup?: {
        location?: {
            name?: string;
        };
    };
}

/**
 * Map portal VisualLineOfSight enum to the SMS-expected string union.
 * Both sides use the same values; null is returned for absent/unknown values.
 */
function toVisualLineOfSight(
    value: VisualLineOfSight | null | undefined
): MissionContext['visualLineOfSight'] {
    switch (value) {
        case 'VLOS':
            return 'VLOS';
        case 'EVLOS':
            return 'EVLOS';
        case 'BVLOS':
            return 'BVLOS';
        default:
            return null;
    }
}

/**
 * Map portal TimeOfDay enum to the SMS-expected string union.
 * 'DAY_NIGHT' maps to 'TWILIGHT' as the closest SMS equivalent.
 */
function toTimeOfDay(
    value: TimeOfDay | null | undefined
): MissionContext['timeOfDay'] {
    switch (value) {
        case 'DAY':
            return 'DAY';
        case 'NIGHT':
            return 'NIGHT';
        case 'DAY_NIGHT':
            return 'TWILIGHT';
        default:
            return null;
    }
}

/**
 * Extract aircraft nicknames from a persisted mission's resolved craft list.
 * The live-form mapper below can use the separate RPA-type lookup and therefore
 * prefers the actual make/model.
 */
function toRpaTypeNames(crafts: RpaLike[] | null | undefined): string[] {
    if (!crafts || crafts.length === 0) {
        return [];
    }
    const names = crafts
        .map(c => (c.nickname ?? '').trim())
        .filter(n => n.length > 0);
    // Deduplicate
    return [...new Set(names)];
}

/** Returns the maximum maxTakeOffWeight (grams) across the given crafts, or null if unavailable. */
function toMaxRpaWeightGrams(
    crafts: RpaLike[] | null | undefined,
    lookupType: (rpaTypeId: number) => RpaTypeLike | undefined
): number | null {
    if (!crafts || crafts.length === 0) {
        return null;
    }
    const weights = crafts
        .map(c =>
            c.rpaTypeId != null
                ? lookupType(c.rpaTypeId)?.performanceSpecifications
                      ?.maxTakeOffWeight
                : undefined
        )
        .filter((w): w is number => w != null);
    return weights.length > 0 ? Math.max(...weights) : null;
}

function toSelectedRpaTypeNames(
    craftIds: number[],
    rpas: RpaLike[],
    rpaTypes: RpaTypeLike[]
): string[] {
    const selectedRpas = rpas.filter(rpa => craftIds.includes(rpa.id));
    const names = selectedRpas
        .map(rpa => {
            const type = rpaTypes.find(item => item.id === rpa.rpaTypeId);
            const typeName = `${type?.make ?? ''} ${type?.model ?? ''}`.trim();
            return typeName || rpa.nickname?.trim();
        })
        .filter((name): name is string => !!name);
    return [...new Set(names)];
}

/** Resolve the primary pilot (RPIC) id from the crew list. */
function toPilotId(
    missionCrewDetails: CrewDetailLike[] | null | undefined
): number | null {
    if (!missionCrewDetails) {
        return null;
    }
    const rpic = missionCrewDetails.find(
        crew => crew.role?.coreRole === 'PILOT_IN_COMMAND'
    );
    return rpic?.person?.id ?? null;
}

/** Map crew details to the flat id+role shape expected by MissionContext. */
function toCrew(
    missionCrewDetails: CrewDetailLike[] | null | undefined
): Array<{ personId: number; coreRole: string }> {
    if (!missionCrewDetails) {
        return [];
    }
    return (
        missionCrewDetails
            .filter(c => c.person?.id != null && c.role?.coreRole != null)
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            .map(c => ({
                personId: c.person!.id!,
                coreRole: c.role!.coreRole!
            }))
    );
}

/** Portal stores height in feet AGL; SMS expects metres. */
function feetToMetres(feet: number | null | undefined): number | null {
    return feet != null ? Math.round(feet * 0.3048 * 10) / 10 : null;
}

/** Map a portal DisplayableMissionDto to the SMS MissionContext shape. */
export function toSmsMissionContext(mission: MissionLike): MissionContext {
    return {
        rpaTypeNames: toRpaTypeNames(mission.crafts),
        rpaWeightGrams: toMaxRpaWeightGrams(
            mission.crafts,
            id => mission.rpaTypes?.[id]
        ),
        rpaCount: mission.crafts?.length ?? 0,
        pilotId: toPilotId(mission.missionCrewDetails),
        crew: toCrew(mission.missionCrewDetails),
        visualLineOfSight: toVisualLineOfSight(mission.visualLineOfSight),
        timeOfDay: toTimeOfDay(mission.timeOfDay),
        maximumHeight: feetToMetres(mission.maximumHeight),
        operationType: mission.operationType ?? null,
        locationName: mission.locationName ?? null
    };
}

/** Maps the mission editor's current, potentially unsaved form state. */
export function toSmsMissionContextFromForm(
    value: LiveMissionContextValue,
    rpas: RpaLike[],
    rpaTypes: RpaTypeLike[],
    missionCrewDetails: CrewDetailLike[] | null
): MissionContext {
    const selectedRpas = (rpas ?? []).filter(r =>
        (value.craftIds ?? []).includes(r.id)
    );
    return {
        rpaTypeNames: toSelectedRpaTypeNames(
            value.craftIds ?? [],
            rpas ?? [],
            rpaTypes ?? []
        ),
        rpaWeightGrams: toMaxRpaWeightGrams(selectedRpas, id =>
            (rpaTypes ?? []).find(t => t.id === id)
        ),
        rpaCount: value.craftIds?.length ?? 0,
        pilotId: toPilotId(missionCrewDetails),
        crew: toCrew(missionCrewDetails),
        visualLineOfSight: toVisualLineOfSight(value.visualLineOfSight),
        timeOfDay: toTimeOfDay(value.timeOfDay),
        maximumHeight: feetToMetres(value.maximumHeight),
        operationType: value.operationType ?? null,
        locationName: value.missionObjectivesGroup?.location?.name ?? null
    };
}
